/**
 * T407 — AdminAuditLogViewer.
 *
 * Filterable list of admin_audit_log entries. Filters applied client-side
 * over the currently loaded page (server-side filter parameters would
 * require backend extensions beyond T406's current contract — defer).
 * Pagination + scope toggle (`tenant` / `global`) use the real API query
 * params.
 */
import { useMemo, useState } from 'react';
import { Button, GlassPanel, LoadingSpinner, EmptyState } from '../ui';
import {
  useAdminAuditLog,
  type AdminAuditLogEntryDto,
} from '../../services/api/admin-audit';
import { formatDateTimeBerlin } from '../../lib/format-date';

function formatTs(ts: string): string {
  return formatDateTimeBerlin(ts) || ts;
}

function short(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

export function AdminAuditLogViewer() {
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<'tenant' | 'global'>('tenant');
  const [actorFilter, setActorFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [targetTenantFilter, setTargetTenantFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const limit = 50;
  const { data, isLoading, error } = useAdminAuditLog({ page, limit, scope });

  const filtered: AdminAuditLogEntryDto[] = useMemo(() => {
    const rows = data?.items ?? [];
    return rows.filter((r) => {
      if (actorFilter && !r.actorUserId.toLowerCase().includes(actorFilter.toLowerCase())) {
        return false;
      }
      if (actionFilter && !r.action.toLowerCase().includes(actionFilter.toLowerCase())) {
        return false;
      }
      if (
        targetTenantFilter &&
        !(r.targetTenantId ?? '').toLowerCase().includes(targetTenantFilter.toLowerCase())
      ) {
        return false;
      }
      if (dateFrom) {
        if (r.createdAt.slice(0, 10) < dateFrom) return false;
      }
      if (dateTo) {
        if (r.createdAt.slice(0, 10) > dateTo) return false;
      }
      return true;
    });
  }, [data, actorFilter, actionFilter, targetTenantFilter, dateFrom, dateTo]);

  if (error) {
    return (
      <div className="text-red-400 bg-red-900/20 border border-red-800 rounded p-4 text-sm">
        Fehler: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-audit-viewer">
      <div className="flex gap-2 flex-wrap items-end">
        <div>
          <label className="block text-xs text-white/50 mb-1">Bereich</label>
          <select
            value={scope}
            onChange={(e) => {
              setPage(1);
              setScope(e.target.value as 'tenant' | 'global');
            }}
            className="px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm"
          >
            <option value="tenant">Aktueller Mandant</option>
            <option value="global">Global (keine Mandanten-Zuordnung)</option>
          </select>
        </div>
        <FilterInput
          label="Actor (User-ID)"
          value={actorFilter}
          onChange={setActorFilter}
          testid="filter-actor"
        />
        <FilterInput
          label="Action"
          value={actionFilter}
          onChange={setActionFilter}
          placeholder="user.create, tenant.update…"
          testid="filter-action"
        />
        <FilterInput
          label="Ziel-Mandant (UUID)"
          value={targetTenantFilter}
          onChange={setTargetTenantFilter}
          testid="filter-target-tenant"
        />
        <div>
          <label className="block text-xs text-white/50 mb-1">Von</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm"
            data-testid="filter-date-from"
          />
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1">Bis</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm"
            data-testid="filter-date-to"
          />
        </div>
        <Button
          variant="ghost"
          onClick={() => {
            setActorFilter('');
            setActionFilter('');
            setTargetTenantFilter('');
            setDateFrom('');
            setDateTo('');
          }}
        >
          Filter zurücksetzen
        </Button>
      </div>

      {isLoading && <LoadingSpinner />}

      {!isLoading && filtered.length === 0 && (
        <EmptyState
          title="Keine Einträge"
          description={
            data && data.items.length > 0
              ? 'Filter schliesst alle Einträge aus. Filter zurücksetzen um alle zu sehen.'
              : 'Noch keine Admin-Aktionen im gewählten Bereich.'
          }
        />
      )}

      {!isLoading && filtered.length > 0 && (
        <GlassPanel
          variant="subtle"
          padding="none"
          className="overflow-hidden"
          data-testid="admin-audit-log-surface"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 text-sm">
            <thead>
              <tr className="text-left text-xs font-medium text-white/50 uppercase tracking-wider">
                <th className="px-3 py-2">Zeit</th>
                <th className="px-3 py-2">Actor</th>
                <th className="px-3 py-2">Aktion</th>
                <th className="px-3 py-2">Mandant</th>
                <th className="px-3 py-2">Ziel-Mandant</th>
                <th className="px-3 py-2">Ziel-User</th>
                <th className="px-3 py-2">Ressource</th>
                <th className="px-3 py-2">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-white/5"
                  data-testid={`admin-audit-row-${r.id}`}
                >
                  <td className="px-3 py-2 text-white/70 whitespace-nowrap">
                    {formatTs(r.createdAt)}
                  </td>
                  <td
                    className="px-3 py-2 text-white/80 font-mono text-xs"
                    title={r.actorUserId}
                  >
                    {short(r.actorUserId)}
                  </td>
                  <td className="px-3 py-2 text-white">{r.action}</td>
                  <td
                    className="px-3 py-2 text-white/70 font-mono text-xs"
                    title={r.tenantId ?? '—'}
                  >
                    {short(r.tenantId)}
                  </td>
                  <td
                    className="px-3 py-2 text-white/70 font-mono text-xs"
                    title={r.targetTenantId ?? '—'}
                  >
                    {short(r.targetTenantId)}
                  </td>
                  <td
                    className="px-3 py-2 text-white/70 font-mono text-xs"
                    title={r.targetUserId ?? '—'}
                  >
                    {short(r.targetUserId)}
                  </td>
                  <td className="px-3 py-2 text-white/70">
                    {r.targetResourceType ? (
                      <span>
                        {r.targetResourceType} ·{' '}
                        <span className="font-mono text-xs" title={r.targetResourceId ?? ''}>
                          {short(r.targetResourceId)}
                        </span>
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-white/50 font-mono text-xs">
                    {r.ipAddress ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </div>
        </GlassPanel>
      )}

      {data && data.pagination.totalPages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <Button
            variant="ghost"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="admin-audit-prev"
          >
            ← Vorherige
          </Button>
          <span className="text-white/50">
            Seite {data.pagination.page} / {data.pagination.totalPages} · {data.pagination.total}{' '}
            gesamt
          </span>
          <Button
            variant="ghost"
            onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
            disabled={page >= data.pagination.totalPages}
            data-testid="admin-audit-next"
          >
            Nächste →
          </Button>
        </div>
      )}
    </div>
  );
}

function FilterInput({
  label,
  value,
  onChange,
  placeholder,
  testid,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testid?: string;
}) {
  return (
    <div>
      <label className="block text-xs text-white/50 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="px-3 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm min-w-[180px]"
        data-testid={testid}
      />
    </div>
  );
}
